import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Package, CheckCircle2, Delete, Loader2, WifiOff } from "lucide-react";

export default function Kiosk() {
  const { companyCode, storeCode } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [kin, setKin] = useState("");
  const [status, setStatus] = useState(null); // {type, message}
  const [submitting, setSubmitting] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);

  useEffect(() => {
    api.get(`/kiosk/${companyCode}/${storeCode}`).then((r) => setInfo(r.data)).catch((e) => setError(apiError(e)));
  }, [companyCode, storeCode]);

  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  const press = (d) => { setStatus(null); setKin((k) => (k + d).slice(0, 20)); };
  const back = () => setKin((k) => k.slice(0, -1));

  const submit = async () => {
    const clean = kin.trim().toUpperCase().replace(/\s/g, "");
    if (!clean) { setStatus({ type: "error", message: "Please enter your KIN number" }); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/kiosk/${companyCode}/${storeCode}/checkin`, { kin_number: clean });
      setStatus({ type: "success", message: data.message });
      setKin("");
      setTimeout(() => setStatus(null), 5000);
    } catch (e) {
      setStatus({ type: "error", message: apiError(e) });
    } finally {
      setSubmitting(false);
    }
  };

  if (error) return <div className="min-h-screen flex items-center justify-center bg-white text-black text-2xl font-display">{error}</div>;
  if (!info) return <div className="min-h-screen flex items-center justify-center bg-white"><Loader2 className="animate-spin text-black" size={40} /></div>;

  return (
    <div className="kiosk-scope min-h-screen bg-white text-[#0A0A0A] flex flex-col">
      {!online && (
        <div className="bg-[#7F1D1D] text-white text-center py-2 flex items-center justify-center gap-2 text-lg">
          <WifiOff size={18} /> Connection lost. Some updates may be delayed.
        </div>
      )}
      <header className="flex items-center justify-between px-10 py-6 border-b border-black/10">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-black flex items-center justify-center rounded-sm">
            <Package size={26} className="text-white" />
          </div>
          <div>
            <div className="font-display text-3xl font-extrabold tracking-tight leading-none">{info.company_name}</div>
            <div className="text-sm uppercase tracking-widest text-black/50">{info.store_name}</div>
          </div>
        </div>
        <span className="font-mono-t text-sm text-black/40">{info.store_code}</span>
      </header>

      <div className="flex-1 flex flex-col items-center justify-center px-6 py-10 max-w-2xl mx-auto w-full">
        <h1 className="font-display text-6xl sm:text-7xl font-extrabold tracking-tight text-center leading-none">Package Collection<br />Check-In</h1>
        <p className="text-xl text-black/60 mt-6 text-center">Enter your KIN number below to notify the warehouse that you have arrived.</p>

        {status?.type === "success" ? (
          <div className="mt-10 w-full bg-[#22C55E]/10 border-2 border-[#22C55E] p-10 text-center rounded-sm">
            <CheckCircle2 size={64} className="text-[#22C55E] mx-auto" />
            <div className="font-display text-3xl font-bold mt-4">{status.message}</div>
          </div>
        ) : (
          <>
            <div className="mt-8 w-full">
              <div className="h-24 border-b-4 border-black flex items-center justify-center">
                <span className="font-mono-t text-6xl font-bold tracking-widest" data-testid="kiosk-kin-display">
                  {kin || <span className="text-black/25">KIN</span>}
                </span>
              </div>
              {status?.type === "error" && <p className="text-[#FF3B30] text-center mt-3 text-lg font-medium" data-testid="kiosk-error">{status.message}</p>}
            </div>

            <div className="grid grid-cols-3 gap-3 mt-8 w-full max-w-md">
              {["1","2","3","4","5","6","7","8","9"].map((d) => (
                <button key={d} onClick={() => press(d)} data-testid={`kiosk-key-${d}`}
                  className="h-20 bg-black/5 hover:bg-black/10 active:bg-black/20 rounded-sm font-mono-t text-4xl font-bold transition-colors duration-150">{d}</button>
              ))}
              <button onClick={() => press("KIN")} className="h-20 bg-black/5 hover:bg-black/10 rounded-sm font-display text-xl font-bold transition-colors duration-150" data-testid="kiosk-key-prefix">KIN</button>
              <button onClick={() => press("0")} className="h-20 bg-black/5 hover:bg-black/10 rounded-sm font-mono-t text-4xl font-bold transition-colors duration-150" data-testid="kiosk-key-0">0</button>
              <button onClick={back} className="h-20 bg-black/5 hover:bg-black/10 rounded-sm flex items-center justify-center transition-colors duration-150" data-testid="kiosk-key-back"><Delete size={30} /></button>
            </div>

            <button onClick={submit} disabled={submitting} data-testid="kiosk-checkin-btn"
              className="mt-8 w-full max-w-md h-24 bg-black text-white rounded-full font-display text-4xl font-bold tracking-tight hover:bg-[#FF3B30] active:scale-[0.99] transition-colors duration-200 flex items-center justify-center gap-3">
              {submitting ? <Loader2 className="animate-spin" size={32} /> : "CHECK IN"}
            </button>
          </>
        )}
      </div>
      <footer className="text-center py-4 text-black/30 text-sm">Your KIN number is confidential. Please keep it private.</footer>
    </div>
  );
}
